// Packages
import React, { useCallback, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  EntLayoutSideNav,
  IEntNavItem,
  IEntUserMenuItem,
} from '@ent-ui-kit/react';

// Components
import { navItems } from './navItems';

// Hooks
import { useAnalytics } from 'hooks/useAnalytics';
import { useAuthUser } from 'hooks/useAuthUser';
import { useRouteUtils } from 'hooks/useRouteUtils';
import { useAuthSession } from 'hooks/useAuthSession';

// Context
import { currentPageTitle } from 'contexts/utils/analyticsUtils';

// Utils
import { getClientInfo } from 'utils/generalUtils';

interface ILayoutProps {
  children: React.ReactNode;
  className?: string;
  loading?: boolean;
}

const AuthLayout = ({ children, className = '', loading }: ILayoutProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { authUser } = useAuthUser();
  const { signOut } = useAuthSession();
  const { checkRoutePermissions } = useRouteUtils();
  const { trackButtonEvent } = useAnalytics();

  const userMenuItems: IEntUserMenuItem[] = [
    { label: 'Profile', path: '/profile', onClick: () => navigate('/profile') },
  ];

  const handleFooterLinkClick = useCallback(
    (label: string): void => {
      trackButtonEvent(
        { itemClicked: label, pageRegion: 'Footer Navigation' },
        { siteSection: currentPageTitle() }
      );
    },
    [trackButtonEvent]
  );

  useEffect(() => {
    const clientInfo = getClientInfo();
    if (clientInfo.os === 'Windows') {
      document.getElementsByTagName('body')[0].classList.add('windows');
    }
  }, []);

  const entNavItems: IEntNavItem[] = useMemo(
    () =>
      navItems
        .filter((item) => {
          if (!item.permissions) return true;
          return checkRoutePermissions(item.permissions);
        })
        .map((item) => ({
          label: item.label,
          path: item.path,
          icon: item.icon ?? '',
          isActiveRoute: location.pathname.startsWith(item.path),
        })),
    [checkRoutePermissions, location.pathname]
  );

  const handleNavItemClick = useCallback(
    (item: IEntNavItem) => {
      navigate(item.path);
    },
    [navigate]
  );

  return (
    <EntLayoutSideNav
      className={classNames({ layout: true, [`${className}`]: !!className })}
      title="Dexter Dev Labs"
      isLoading={loading}
      navItems={entNavItems}
      onNavItemClick={handleNavItemClick}
      onFooterLinkClick={handleFooterLinkClick}
      userMenu={{
        userName: authUser ? `${authUser.firstName} ${authUser.lastName}` : '',
        menuItems: userMenuItems,
        onSignOut: signOut,
      }}
    >
      {children}
    </EntLayoutSideNav>
  );
};

export default AuthLayout;
